import MapLogger from './MapLogger';

/**
 * Enhanced error handler for managing project display on map
 * Provides comprehensive error handling with retry and user-friendly messages
 */
class MapErrorHandler {
  static retryAttempts = 3;
  static retryDelay = 1000; // 1 second

  /**
   * Handle layer errors
   * @param {Error} error - Error object
   * @param {string} layerName - Layer name
   * @param {Object} context - Additional context
   */
  static handleLayerError(error, layerName, context = {}) {
    MapLogger.logError('layer', `Error in ${layerName}`, error, context);
    
    // Determine error type and appropriate response
    const errorType = this.categorizeError(error);
    
    switch (errorType) {
      case 'NETWORK_ERROR':
        this.handleNetworkError(error, layerName, context);
        break;
      case 'MAP_LAYER_ERROR':
        this.handleMapLayerError(error, layerName, context);
        break;
      case 'DATA_ERROR':
        this.handleDataError(error, layerName, context);
        break;
      case 'PERMISSION_ERROR':
        this.handlePermissionError(error, layerName, context);
        break;
      default:
        this.handleGenericError(error, layerName, context);
    }
  }

  /**
   * Handle project loading errors
   * @param {Error} error - Error object
   * @param {number} projectId - Project ID
   * @param {Object} context - Additional context
   */
  static handleProjectLoadError(error, projectId, context = {}) {
    MapLogger.logError('project', `Error loading project ${projectId}`, error, context);
    
    const errorType = this.categorizeError(error);
    
    switch (errorType) {
      case 'NETWORK_ERROR':
        this.scheduleRetry(() => this.retryProjectLoad(projectId, context), 'project-load');
        break;
      case 'DATA_ERROR':
        this.handleProjectDataError(error, projectId, context);
        break;
      case 'PERMISSION_ERROR':
        this.handleProjectPermissionError(error, projectId, context);
        break;
      default:
        this.handleGenericProjectError(error, projectId, context);
    }
  }

  /**
   * Handle filter errors
   * @param {Error} error - Error object
   * @param {string} filterType - Filter type
   * @param {Object} context - Additional context
   */
  static handleFilterError(error, filterType, context = {}) {
    MapLogger.logError('filter', `Error applying ${filterType} filter`, error, context);
    
    // Reset filter to previous state
    this.resetFilter(filterType, context);
  }

  /**
   * Categorize errors
   * @param {Error} error - Error object
   * @returns {string} - Error type
   */
  static categorizeError(error) {
    const message = error.message.toLowerCase();
    
    if (message.includes('network') || message.includes('fetch') || message.includes('timeout')) {
      return 'NETWORK_ERROR';
    }
    
    if (message.includes('layer') || message.includes('source') || message.includes('style')) {
      return 'MAP_LAYER_ERROR';
    }
    
    if (message.includes('data') || message.includes('json') || message.includes('parse')) {
      return 'DATA_ERROR';
    }
    
    if (message.includes('permission') || message.includes('unauthorized') || message.includes('forbidden')) {
      return 'PERMISSION_ERROR';
    }
    
    return 'UNKNOWN_ERROR';
  }

  /**
   * Handle network errors
   * @param {Error} error - Error object
   * @param {string} layerName - Layer name
   * @param {Object} context - Additional context
   */
  static handleNetworkError(error, layerName, context) {
    MapLogger.logWarning('network', `Network error for ${layerName}`, {
      error: error.message,
      context
    });
    
    // Show message to user
    this.showUserMessage('network_error', {
      layer: layerName,
      message: 'Check your internet connection and try again'
    });
  }

  /**
   * Handle map layer errors
   * @param {Error} error - Error object
   * @param {string} layerName - Layer name
   * @param {Object} context - Additional context
   */
  static handleMapLayerError(error, layerName, context) {
    MapLogger.logWarning('map-layer', `Map layer error for ${layerName}`, {
      error: error.message,
      context
    });
    
    // Try to recreate the layer
    this.scheduleRetry(() => this.recreateLayer(layerName, context), 'layer-recreation');
  }

  /**
   * Handle data errors
   * @param {Error} error - Error object
   * @param {string} layerName - Layer name
   * @param {Object} context - Additional context
   */
  static handleDataError(error, layerName, context) {
    MapLogger.logWarning('data', `Data error for ${layerName}`, {
      error: error.message,
      context
    });
    
    // Show message to user
    this.showUserMessage('data_error', {
      layer: layerName,
      message: 'Error loading data'
    });
  }

  /**
   * Handle permission errors
   * @param {Error} error - Error object
   * @param {string} layerName - Layer name
   * @param {Object} context - Additional context
   */
  static handlePermissionError(error, layerName, context) {
    MapLogger.logWarning('permission', `Permission error for ${layerName}`, {
      error: error.message,
      context
    });
    
    // Show message to user
    this.showUserMessage('permission_error', {
      layer: layerName,
      message: 'You do not have permission to access this data'
    });
  }

  /**
   * Handle generic errors
   * @param {Error} error - Error object
   * @param {string} layerName - Layer name
   * @param {Object} context - Additional context
   */
  static handleGenericError(error, layerName, context) {
    MapLogger.logWarning('generic', `Generic error for ${layerName}`, {
      error: error.message,
      context
    });
    
    // Show generic message to user
    this.showUserMessage('generic_error', {
      layer: layerName,
      message: 'An unexpected error occurred'
    });
  }

  /**
   * Handle project data errors
   * @param {Error} error - Error object
   * @param {number} projectId - Project ID
   * @param {Object} context - Additional context
   */
  static handleProjectDataError(error, projectId, context) {
    MapLogger.logWarning('project-data', `Data error for project ${projectId}`, {
      error: error.message,
      context
    });
    
    this.showUserMessage('project_data_error', {
      projectId,
      message: 'Error loading project data'
    });
  }

  /**
   * Handle project permission errors
   * @param {Error} error - Error object
   * @param {number} projectId - Project ID
   * @param {Object} context - Additional context
   */
  static handleProjectPermissionError(error, projectId, context) {
    MapLogger.logWarning('project-permission', `Permission error for project ${projectId}`, {
      error: error.message,
      context
    });
    
    this.showUserMessage('project_permission_error', {
      projectId,
      message: 'You do not have permission to access this project'
    });
  }

  /**
   * Handle generic project errors
   * @param {Error} error - Error object
   * @param {number} projectId - Project ID
   * @param {Object} context - Additional context
   */
  static handleGenericProjectError(error, projectId, context) {
    MapLogger.logWarning('project-generic', `Generic error for project ${projectId}`, {
      error: error.message,
      context
    });
    
    this.showUserMessage('project_generic_error', {
      projectId,
      message: 'An error occurred while loading the project'
    });
  }

  /**
   * Schedule retry
   * @param {Function} retryFunction - Retry function
   * @param {string} operation - Operation
   */
  static scheduleRetry(retryFunction, operation) {
    let attempts = 0;
    
    const retry = () => {
      attempts++;
      
      if (attempts <= this.retryAttempts) {
        MapLogger.logWarning('retry', `Retrying ${operation} (attempt ${attempts}/${this.retryAttempts})`);
        
        setTimeout(() => {
          try {
            retryFunction();
          } catch (error) {
            if (attempts < this.retryAttempts) {
              retry();
            } else {
              MapLogger.logError('retry', `Failed ${operation} after ${this.retryAttempts} attempts`, error);
            }
          }
        }, this.retryDelay * attempts);
      } else {
        MapLogger.logError('retry', `Max retry attempts reached for ${operation}`);
      }
    };
    
    retry();
  }

  /**
   * Retry project load
   * @param {number} projectId - Project ID
   * @param {Object} context - Additional context
   */
  static async retryProjectLoad(projectId, context) {
    // This function should be called from the appropriate service
    MapLogger.logOperationStart('retry-project-load', { projectId, context });
  }

  /**
   * Recreate layer
   * @param {string} layerName - Layer name
   * @param {Object} context - Additional context
   */
  static async recreateLayer(layerName, context) {
    // This function should be called from the appropriate service
    MapLogger.logOperationStart('recreate-layer', { layerName, context });
  }

  /**
   * Reset filter
   * @param {string} filterType - Filter type
   * @param {Object} context - Additional context
   */
  static resetFilter(filterType, context) {
    MapLogger.logOperationStart('reset-filter', { filterType, context });
    
    // Reset filter to default state
    // This function should be called from the appropriate service
  }

  /**
   * Show message to user
   * @param {string} messageType - Message type
   * @param {Object} data - Data
   */
  static showUserMessage(messageType, data) {
    const messages = {
      'network_error': 'Check your internet connection and try again',
      'data_error': 'Error loading data',
      'permission_error': 'You do not have permission to access this data',
      'generic_error': 'An unexpected error occurred',
      'project_data_error': 'Error loading project data',
      'project_permission_error': 'You do not have permission to access this project',
      'project_generic_error': 'An error occurred while loading the project'
    };
    
    const message = messages[messageType] || 'An unexpected error occurred';
    
    // Show notification to user
    console.warn(`🚨 User Message: ${message}`, data);
    
    // Can add notification system here
    // notificationService.show(message, 'error');
  }

  /**
   * Log error statistics
   * @param {string} errorType - Error type
   * @param {Object} metadata - Metadata
   */
  static logErrorStats(errorType, metadata = {}) {
    const stats = {
      timestamp: new Date().toISOString(),
      errorType,
      metadata
    };
    
    // Can send statistics to analytics service
    MapLogger.logWarning('error-stats', `Error statistics logged`, stats);
  }

  /**
   * Clean up old errors
   */
  static cleanupOldErrors() {
    // Clean up old errors from memory
    MapLogger.logOperationStart('cleanup-old-errors');
  }
}

export default MapErrorHandler;
